import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { ClipboardList, RefreshCw, AlertTriangle, Lock } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { getFacilitySettlingRows } from '../../services/workflow/FacilityWorkflowSummary';

/**
 * Facility-level view: Stage 6 + 7A checklist completion, blockers, overdue counts.
 * Data from local workflow persistence (same store as resident CRM checklists).
 */
export default function SettlingInDashboardPanel() {
  const { state } = useAppContext();
  const { selectedFacility } = state;
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    refresh();
  }, [selectedFacility, refresh]);

  const rows = useMemo(() => {
    void tick;
    if (!selectedFacility || selectedFacility === 'all') return [];
    return getFacilitySettlingRows(selectedFacility);
  }, [selectedFacility, tick]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[SettlingInDashboardPanel] rows', { facility: selectedFacility, count: rows.length });
    }
  }, [selectedFacility, rows.length]);

  if (!selectedFacility || selectedFacility === 'all') {
    return null;
  }

  return (
    <div
      className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50/90 to-white p-5 shadow-sm"
      data-testid="settling-dashboard-panel"
    >
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-emerald-700" aria-hidden />
          <div>
            <h3 className="text-base font-bold text-slate-900">Admission & settling-in (facility)</h3>
            <p className="text-xs text-slate-600">
              Progress on Stage 6 and 7A tasks per resident (local persistence).{' '}
              <span className="font-mono">{selectedFacility}</span>
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-xs font-semibold text-emerald-800 shadow-sm hover:bg-emerald-50"
          data-testid="settling-dashboard-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          Refresh
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-slate-600">
          No workflow rows yet for this facility. Open a resident profile → CRM tab → Stage 6 or Stage 7
          (7A–7E) to materialise checklists; this table then shows admission/settling progress.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
                <th className="px-3 py-2">Resident</th>
                <th className="px-3 py-2">Room</th>
                <th className="px-3 py-2">Admission</th>
                <th className="px-3 py-2">Progress</th>
                <th className="px-3 py-2">Blockers</th>
                <th className="px-3 py-2">Overdue</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.residentId} className="border-b border-slate-100 last:border-0">
                  <td className="px-3 py-2 font-medium text-slate-900">{row.name}</td>
                  <td className="px-3 py-2 text-slate-700">{row.room || '—'}</td>
                  <td className="px-3 py-2 text-slate-700">{row.admissionDate || '—'}</td>
                  <td className="px-3 py-2">
                    <span className="font-semibold text-emerald-800">{row.percent}%</span>
                    <span className="text-slate-500">
                      {' '}
                      ({row.completed}/{row.total})
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {row.blockerCount > 0 ? (
                      <span className="inline-flex items-center gap-1 text-amber-800" title={row.blockerLabels.join('; ')}>
                        <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        {row.blockerCount}
                      </span>
                    ) : (
                      <span className="text-slate-400">0</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {row.overdueCount > 0 ? (
                      <span className="inline-flex items-center gap-1 font-medium text-red-700">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        {row.overdueCount}
                      </span>
                    ) : (
                      <span className="text-slate-400">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
